import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Mail, Delete, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function Login() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("password"); // password | pin
  const [step, setStep] = useState("login"); // login | 2fa
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pin, setPin] = useState("");
  const [code, setCode] = useState("");
  const [mfaToken, setMfaToken] = useState("");
  const [loading, setLoading] = useState(false);

  const finish = (data) => { setUser(data); navigate("/pos"); };

  const submitPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.requires_2fa) { setMfaToken(data.mfa_token); setStep("2fa"); toast.info("Enter your 2FA code"); }
      else finish(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Login failed"); }
    finally { setLoading(false); }
  };

  const submit2fa = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/2fa/verify-login", { mfa_token: mfaToken, code });
      finish(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const submitPin = async (thePin) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/pin-login", { pin: thePin });
      finish(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setPin(""); }
    finally { setLoading(false); }
  };

  const pressPin = (d) => {
    if (d === "del") return setPin((p) => p.slice(0, -1));
    const next = (pin + d).slice(0, 6);
    setPin(next);
    if (next.length >= 4 && d !== "del") {
      // allow up to 6, auto-submit at 4 only if user taps ok; keep manual
    }
  };

  return (
    <div className="min-h-screen w-full flex bg-white">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between w-[45%] bg-black text-white p-14 relative overflow-hidden">
        <div className="flex items-center gap-3">
          <div className="h-14 w-14 rounded-xl bg-white flex items-center justify-center overflow-hidden p-1.5 shrink-0">
            <img src="/demodog-logo.jpeg" alt="Demodog World Inc." className="h-full w-full object-contain" />
          </div>
          <div className="leading-tight">
            <span className="block font-heading font-extrabold text-2xl tracking-tight">Demodog World Inc.</span>
            <span className="block text-white/60 text-xs font-semibold tracking-widest uppercase">ERP · Ontario</span>
          </div>
        </div>
        <div>
          <h1 className="font-heading text-5xl font-extrabold tracking-tighter leading-[1.05]">Every part.<br />Every truck.<br />One system.</h1>
          <p className="mt-6 text-white/60 text-lg max-w-md">The complete truck &amp; heavy-duty parts platform — catalog, POS, online store, inventory, invoicing &amp; payments in one place.</p>
        </div>
        <div className="flex items-center gap-2 text-white/50 text-sm">
          <ShieldCheck size={18} /> Bank-grade security · 2FA · Encrypted at rest
        </div>
        <img src="https://images.unsplash.com/photo-1586191552066-d52dd1e3af86?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" alt="" className="absolute -bottom-10 -right-10 w-80 h-80 object-cover rounded-3xl opacity-20" />
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="h-12 w-12 rounded-xl bg-white border border-zinc-200 flex items-center justify-center overflow-hidden p-1 shrink-0"><img src="/demodog-logo.jpeg" alt="Demodog World Inc." className="h-full w-full object-contain" /></div>
            <div className="leading-tight">
              <span className="block font-heading font-extrabold text-xl tracking-tight">Demodog World Inc.</span>
              <span className="block text-zinc-400 text-[11px] font-semibold tracking-widest uppercase">ERP · Ontario</span>
            </div>
          </div>

          <div className="flex gap-2 mb-8 p-1 bg-secondary rounded-xl">
            <button onClick={() => { setMode("password"); setStep("login"); }} data-testid="tab-password"
              className={`flex-1 h-11 rounded-lg text-sm font-semibold transition ${mode === "password" ? "bg-white shadow-sm" : "text-muted-foreground"}`}>
              Email & Password
            </button>
            <button onClick={() => setMode("pin")} data-testid="tab-pin"
              className={`flex-1 h-11 rounded-lg text-sm font-semibold transition ${mode === "pin" ? "bg-white shadow-sm" : "text-muted-foreground"}`}>
              Staff PIN
            </button>
          </div>

          {mode === "password" && step === "login" && (
            <form onSubmit={submitPassword} className="space-y-5" data-testid="login-form">
              <h2 className="font-heading text-3xl font-bold tracking-tight">Welcome back</h2>
              <div>
                <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Email</label>
                <div className="mt-2 relative">
                  <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                    className="w-full h-13 py-3 pl-12 pr-4 rounded-xl border border-input bg-white text-base focus:outline-none focus:ring-2 focus:ring-black" />
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Password</label>
                <div className="mt-2 relative">
                  <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                    className="w-full h-13 py-3 pl-12 pr-4 rounded-xl border border-input bg-white text-base focus:outline-none focus:ring-2 focus:ring-black" />
                </div>
              </div>
              <button data-testid="login-submit" disabled={loading} type="submit"
                className="w-full h-14 rounded-xl bg-black text-white font-semibold text-base transition active:scale-[0.98] disabled:opacity-50">
                {loading ? "Signing in…" : "Sign In"}
              </button>
            </form>
          )}

          {mode === "password" && step === "2fa" && (
            <form onSubmit={submit2fa} className="space-y-5" data-testid="twofa-form">
              <h2 className="font-heading text-3xl font-bold tracking-tight">Two-factor</h2>
              <p className="text-muted-foreground">Enter the 6-digit code from your authenticator app.</p>
              <input data-testid="twofa-code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                inputMode="numeric" placeholder="000000" autoFocus
                className="w-full h-16 text-center text-3xl tracking-[0.5em] font-heading font-bold rounded-xl border border-input focus:outline-none focus:ring-2 focus:ring-black" />
              <button data-testid="twofa-submit" disabled={loading} type="submit"
                className="w-full h-14 rounded-xl bg-black text-white font-semibold transition active:scale-[0.98] disabled:opacity-50">
                {loading ? "Verifying…" : "Verify & Continue"}
              </button>
            </form>
          )}

          {mode === "pin" && (
            <div className="space-y-6" data-testid="pin-pad">
              <div>
                <h2 className="font-heading text-3xl font-bold tracking-tight">Staff sign in</h2>
                <p className="text-muted-foreground mt-1">Enter your 4-6 digit PIN.</p>
              </div>
              <div className="flex justify-center gap-3">
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className={`h-4 w-4 rounded-full transition ${i < pin.length ? "bg-black" : "bg-secondary border border-border"} ${i >= 4 && pin.length < 5 ? "opacity-40" : ""}`} />
                ))}
              </div>
              <div className="grid grid-cols-3 gap-3">
                {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((d) => (
                  <button key={d} data-testid={`pin-${d}`} onClick={() => pressPin(d)}
                    className="h-16 rounded-xl bg-secondary text-2xl font-heading font-bold transition active:scale-95 active:bg-black active:text-white">{d}</button>
                ))}
                <button onClick={() => pressPin("del")} data-testid="pin-del" className="h-16 rounded-xl bg-secondary flex items-center justify-center transition active:scale-95"><Delete size={22} /></button>
                <button data-testid="pin-0" onClick={() => pressPin("0")} className="h-16 rounded-xl bg-secondary text-2xl font-heading font-bold transition active:scale-95 active:bg-black active:text-white">0</button>
                <button data-testid="pin-submit" disabled={pin.length < 4 || loading} onClick={() => submitPin(pin)}
                  className="h-16 rounded-xl bg-black text-white font-semibold transition active:scale-95 disabled:opacity-40">OK</button>
              </div>
              <p className="text-sm text-muted-foreground text-center">Demo PINs — Owner 4321 · Manager 1111 · Cashier 2222</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
